import { z } from 'zod';
/**
 * T7-003 — QuickBooks Online OAuth Zod schemas (saga pattern parent).
 *
 * These schemas define the request/response shapes for the four QBO OAuth
 * endpoints + the persisted-token payload that lives under
 * `system_config.payload` (configType='qbo_oauth_tokens'). They are
 * consumed by:
 *   - `packages/backend/src/routes/qbo-oauth.ts` (route layer Zod gates)
 *   - `packages/backend/src/repositories/qbo-oauth.ts` (token persistence
 *     + state-token lifecycle)
 *
 * **Architecture (CC-resolved 2026-05-29):** V1 stores access + refresh
 * tokens in Postgres `system_config` (encrypted at rest by Azure). Phase
 * 10B prod cutover migrates to Key Vault per the STUB
 * `T-QBO-TOKENS-KV-MIGRATION`. This file's contract does NOT depend on the
 * backing store; only the repository changes when Key Vault lands.
 */
/**
 * Connection-status payload returned from GET /oauth/status. Discriminated
 * union on `status`:
 *   - `connected`: tokens persisted; access token valid (or refreshable).
 *   - `expired`:   tokens persisted; access token past `tokenExpiresAt`.
 *                  V1 surfaces this so the admin UI can prompt a reconnect.
 *                  Phase 10B will add refresh-token rotation that auto-
 *                  recovers from this state without manual reconnect.
 *   - `not_connected`: no tokens persisted (or disconnect tombstone).
 */
export const QboOauthStatusConnectedSchema = z.object({
    status: z.literal('connected'),
    realmId: z.string(),
    tokenExpiresAt: z.string(), // ISO timestamp (the 1-hour access token; refreshed on use)
    lastRefreshedAt: z.string(), // ISO timestamp
    /**
     * 2026-11-04 trust review (money): the ~100-day refresh-token deadline,
     * the one that ends the connection. Absent when it wasn't recorded.
     */
    refreshTokenExpiresAt: z.string().optional(),
});
/**
 * 2026-11-04 trust review (money): `expired` now means the REFRESH token
 * expired (reconnect needed), never the 1-hour access token, which renews
 * itself on the next use (RIG-461).
 */
export const QboOauthStatusExpiredSchema = z.object({
    status: z.literal('expired'),
    realmId: z.string(),
    expiredAt: z.string(), // ISO timestamp of when the refresh token expired
});
/**
 * 2026-11-04 trust review (money): a connection is stored, but for a
 * company this environment's allow-list (QUICKBOOKS_REALM_ID) refuses, so
 * nothing is read from or written to QuickBooks.
 */
export const QboOauthStatusWrongCompanySchema = z.object({
    status: z.literal('wrong_company'),
    realmId: z.string(),
    expectedRealmId: z.string(),
});
export const QboOauthStatusNotConnectedSchema = z.object({
    status: z.literal('not_connected'),
});
export const QboOauthStatusSchema = z.discriminatedUnion('status', [
    QboOauthStatusConnectedSchema,
    QboOauthStatusExpiredSchema,
    QboOauthStatusWrongCompanySchema,
    QboOauthStatusNotConnectedSchema,
]);
/**
 * Request body for POST /oauth/initiate. `returnTo` is an optional UI
 * redirect-back URL that the callback honors after consent. When absent,
 * the callback redirects to a default admin success page derived from
 * `frontendBaseUrl`.
 */
export const QboOauthInitiateRequestSchema = z.object({
    returnTo: z.string().optional(),
});
/**
 * Response body for POST /oauth/initiate. Returns the Intuit authorize URL
 * the caller should redirect the operator to, plus the state token (also
 * embedded in the URL — surfaced separately so the SPA can persist it for
 * post-callback verification if desired).
 */
export const QboOauthInitiateResponseSchema = z.object({
    authorizeUrl: z.string(),
    state: z.string(),
});
/**
 * Query parameters Intuit sends to GET /oauth/callback after consent.
 * Intuit's spec: `?code=<auth-code>&state=<our-state>&realmId=<qbo-realm-id>`.
 *
 * Note: Intuit may ALSO include `error`/`error_description` on failure;
 * those paths return the user back to the admin UI with an error query
 * param — they do not flow through this schema (the route layer handles
 * the error branch separately before the schema validates).
 */
export const QboOauthCallbackQuerySchema = z.object({
    code: z.string().min(1),
    state: z.string().min(1),
    realmId: z.string().min(1),
});
/**
 * The payload persisted under `system_config.payload` for
 * configType='qbo_oauth_tokens'. Single-row contract: the row id is
 * deterministic ('QBO_OAUTH_TOKENS') and gets replaced wholesale on each
 * successful OAuth or refresh-token rotation.
 *
 * Disconnect writes a tombstone payload (`{ disconnected: true,
 * disconnectedAt }`) so the row persists but `getOauthTokens` returns
 * null. See `QboOauthTombstonePayloadSchema` below.
 */
export const QboOauthTokensPayloadSchema = z.object({
    realmId: z.string().min(1),
    accessToken: z.string().min(1),
    refreshToken: z.string().min(1),
    /** epoch ms — when `accessToken` stops being valid (Intuit: 1 hour). */
    tokenExpiresAt: z.number().int().positive(),
    /**
     * RIG-461 — epoch ms when the REFRESH token stops being valid (Intuit:
     * ~100 days). This is the expiry that actually kills the integration, and it
     * was previously discarded: the callback ignored
     * `x_refresh_token_expires_in` entirely, so the health cron classified the
     * 1-hour ACCESS token expiry in DAYS. `daysRemaining` was therefore always 0,
     * `kind: 'ok'` was mathematically unreachable, and the cron fired an expiry
     * alert every single day forever while nothing watched the real deadline.
     *
     * Optional so tokens stored before this shipped still parse; a missing value
     * means "unknown", which the health check reports rather than guessing.
     */
    refreshTokenExpiresAt: z.number().int().positive().optional(),
    environment: z.enum(['sandbox', 'production']),
    /** ISO timestamp of the most recent successful OAuth or refresh-token rotation. */
    lastRefreshedAt: z.string(),
});
/**
 * Disconnect tombstone — written by POST /oauth/disconnect. The row
 * persists for audit; reads return null.
 */
export const QboOauthTombstonePayloadSchema = z.object({
    disconnected: z.literal(true),
    disconnectedAt: z.string(), // ISO timestamp
});
/**
 * Union of payload shapes the row may hold across its lifetime: an active
 * tokens payload OR a disconnect tombstone. Repository's
 * `getOauthTokens` returns `null` for the tombstone case.
 */
export const QboOauthStoredPayloadSchema = z.union([
    QboOauthTokensPayloadSchema,
    QboOauthTombstonePayloadSchema,
]);
//# sourceMappingURL=qbo-oauth.js.map